import React, { createRef, useState } from "react";
import { showDangerToast, showInfoToast, showSuccessToast } from "../validation/ToastContainer";
import { restoreDatabaseBackup, downloadDatabaseBackup, downloadLogFilesBackup } from "./backupService";
import {
    AlertCircle,
    Download,
    RotateCcw,
    Database,
    HardDrive,
    Server
} from 'lucide-react';
import {
    finishLocalProcessingTask,
    startLocalProcessingTask
} from "../processing-tasks/processingTasksRegistry";

export default function BackupSection() {

    const ref = createRef();
    const [databaseBackupLoading, setDatabaseBackupLoading] = useState(false);
    const [databaseRestoreLoading, setDatabaseRestoreLoading] = useState(false);
    const [logFileBackupLoading, setLogFileBackupLoading] = useState(false);


    const onBackupAppDatabaseClick = async () => {
        if (databaseBackupLoading) {
            return;
        }

        setDatabaseBackupLoading(true);
        const processingTaskId = startLocalProcessingTask({
            id: `database-backup-${Date.now()}`,
            type: "Backup",
            title: "Application Backup",
            details: "Creating application backup"
        });
        showInfoToast("Creating application backup", 'Download');
        try {
            const result = await downloadDatabaseBackup();
            if (result) {
                showSuccessToast("Backup created", 'Download');
            } else {
                showDangerToast("Something went wrong", 'Download');
            }
        } finally {
            finishLocalProcessingTask(processingTaskId);
            setDatabaseBackupLoading(false)
        }
    }

    const onBackupAppLogFilesClick = async () => {
        if (logFileBackupLoading) {
            return;
        }

        setLogFileBackupLoading(true);
        const processingTaskId = startLocalProcessingTask({
            id: `files-backup-${Date.now()}`,
            type: "Backup",
            title: "Files Backup",
            details: "Creating files backup"
        });
        showInfoToast("Creating files backup", 'Download');
        try {
            const result = await downloadLogFilesBackup();
            if (result) {
                showSuccessToast("Backup created", 'Download');
            } else {
                showDangerToast("Something went wrong", 'Download');
            }
        } finally {
            finishLocalProcessingTask(processingTaskId);
            setLogFileBackupLoading(false);
        }
    }

    const onRestoreAppDatabaseChange = (event) => {
        setDatabaseRestoreLoading(true);
        const file = event.target.files[0];
        const processingTaskId = startLocalProcessingTask({
            id: `database-restore-${Date.now()}`,
            type: "Restore",
            title: "System Restore",
            details: file?.name || ""
        });
        restoreDatabaseBackup(file).then(() => {
            event.target.type = '';
            event.target.type = 'file';
            setDatabaseRestoreLoading(false);
        }).finally(() => finishLocalProcessingTask(processingTaskId));
    }

    return (
        <div className="container-fluid mt-4">
            {/* Backup Information Cards */}
            <div className="row mb-4">
                {/* Application Backup Card */}
                <div className="col-lg-6 mb-4 mb-lg-0">
                    <div className="oct_backup-card-v2">
                        <div className="d-flex align-items-start gap-4">
                            <div className="oct_backup-icon-wrapper-v2 app-backup">
                                <Database className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-grow-1">
                                <h3 className="oct_backup-title-v2">Application Backup</h3>
                                <p className="oct_backup-desc-v2">
                                    Backup includes system configuration, security policies, rules, and application settings. This ensures you can restore your system to a known working state.
                                </p>
                                <button
                                    onClick={onBackupAppDatabaseClick}
                                    disabled={databaseBackupLoading}
                                    className="oct_btn-cyan-flex-v2 w-100 justify-content-center"
                                >
                                    <Download className={`w-4 h-4`} />
                                    Download Application Backup
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Files Backup Card */}
                <div className="col-lg-6">
                    <div className="oct_backup-card-v2">
                        <div className="d-flex align-items-start gap-4">
                            <div className="oct_backup-icon-wrapper-v2 files-backup">
                                <HardDrive className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-grow-1">
                                <h3 className="oct_backup-title-v2">Files Backup</h3>
                                <p className="oct_backup-desc-v2">
                                    Backup includes log files, reports, uploaded files, and other data. Essential for maintaining a complete record of system activities and audit trails.
                                </p>
                                <button
                                    onClick={onBackupAppLogFilesClick}
                                    disabled={logFileBackupLoading}
                                    className="oct_btn-cyan-flex-v2 w-100 justify-content-center"
                                >
                                    <Download className={`w-4 h-4`} />
                                    Download Files Backup
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* System Restore Section */}
            <div className="row">
                <div className="col-12">
                    <div className="oct_backup-card-v2">
                        <div className="d-flex align-items-start gap-4">
                            <div className="oct_backup-icon-wrapper-v2 system-restore">
                                <Server className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-grow-1">
                                <h3 className="oct_backup-title-v2">System Restore</h3>
                                <p className="oct_backup-desc-v2">
                                    Restore your system from a previously saved backup. This will restore all application settings, configurations, security policies, and rules.
                                    <span> Warning: This action will override current system settings.</span>
                                </p>

                                <div className="oct_backup-warning-banner-v2">
                                    <div className="oct_warning-header">
                                        <AlertCircle className="w-5 h-5 oct_icon" />
                                        <div className="oct_content">
                                            <p>Important Notes:</p>
                                            <ul>
                                                <li>System restore requires a valid backup file</li>
                                                <li>The restore process may take several minutes</li>
                                                <li>System will automatically reboot after restoration</li>
                                                <li>Ensure no critical operations are running before proceeding</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => ref.current.click()}
                                    disabled={databaseRestoreLoading}
                                    className="oct_btn-cyan-flex-v2"
                                >
                                    <RotateCcw className={`w-4 h-4`} />
                                    Restore System
                                </button>
                                <input className="oct_display-none"
                                    type="file"
                                    name="file"
                                    onChange={onRestoreAppDatabaseChange}
                                    accept=".backup"
                                    ref={ref}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
